<template>
    <div class="mergeFieldsMobile">
        <div class="mergeFieldsMobile__search">
            <span class="mergeFieldsMobile__search__icon">
                <fa-icon icon="search" />
            </span>

            <input v-model="query" type="text" class="input" placeholder="Search..." />
            <a v-if="query" class="mergeFieldsMobile__clear" @click="query = ''">
                <fa-icon icon="times" />
            </a>
        </div>

        <merge-fields-item
            v-for="(item, index) in displayedResults"
            :key="index"
            :item="item"
            :index="index"
            :hasNotIntegration="hasNotIntegration"
            :template="template"
            :checkDate="checkDate"
            @apply-merge-field="applyMergeField"
        />

        <div
            v-if="!displayedResults.length"
            class="mergeFieldsMobile__item mergeFieldsMobile__item--noHover mergeFieldsMobile__item--noResults"
        >
            <div class="help has-text-danger">No Results</div>
        </div>
    </div>
</template>

<script>
import { debounce } from 'lodash';
import moment from 'moment';
import { mapState } from 'pinia';
import stores from 'store/modules';
import MergeFieldsItem from './MergeFieldsItem';

export default {
    components: {
        MergeFieldsItem,
    },

    props: {
        template: { type: Boolean, default: false },
        position: { type: String, default: 'left' },
        icon: { type: [String, Boolean], default: false },
        text: { type: String, default: '' },
        integration: { type: Object },
        conversation: { type: Object },
        default: { type: [Array, String], required: true },
        isIntegration: { type: Boolean, required: false },
        isButton: { type: Boolean, default: false },
        fields: { type: Array, default: () => [] },
        useAllField: { type: Boolean, default: false },
    },

    data: () => ({
        busy: false,
        query: '',
        results: [],
    }),

    computed: {
        classes() {
            return {
                'is-busy': this.busy,
                'has-results': !this.busy && this.query && this.displayedResults !== null,
            };
        },

        displayedResults() {
            let fields = Array.isArray(this.default) ? this.default.map((item) => item) : [];

            if (this.integration) {
                if (!this.template) {
                    if (!this.results.length) {
                        if (!this.query) {
                            return fields.splice(0, 7);
                        }

                        return fields
                            .filter(
                                (o) =>
                                    o.label.toLowerCase().indexOf(this.query.toLowerCase()) !==
                                    -1 && o.value
                            )
                            .splice(0, 7);
                    }

                    if (this.conversation) {
                        return this.results.filter((o) => o.value).splice(0, 7);
                    }

                    return this.results.splice(0, 7);
                }

                if (this.useAllField) {
                    if (!this.query) {
                        return fields.splice(0, 7);
                    } else {
                        return [...fields, ...this.fields]
                            .filter(
                                (o) =>
                                    o.label.toLowerCase().indexOf(this.query.toLowerCase()) !== -1
                            )
                            .splice(0, 7);
                    }
                }

                if (!this.query) {
                    return fields;
                } else {
                    return this.fields
                        .filter(
                            (o) => o.label.toLowerCase().indexOf(this.query.toLowerCase()) !== -1
                        )
                        .splice(0, 7);
                }
            }

            if (this.query) {
                if (this.fields.length) {
                    fields = this.fields.map((item) => item);
                }

                return fields
                    .filter((o) => o.label.toLowerCase().indexOf(this.query.toLowerCase()) !== -1)
                    .splice(0, 7);
            }

            return fields.splice(0, 7);
        },
        ...mapState(stores.User, ['user']),
    },

    watch: {
        query() {
            if (this.integration) {
                this.search();
            }
        },

        default() {
            this.query = '';
        },
    },

    methods: {
        search() {
            if (this.template) {
                return;
            }

            if (!this.query) {
                this.debounceSearch.cancel();
            }

            if (this.query) {
                this.debounceSearch();
            } else {
                this.results = [];
            }
        },

        async handleSearch() {
            try {
                this.busy = true;

                const params = {
                    query: this.query,
                };

                let response;

                if (this.conversation) {
                    response = await this.$store.dispatch('getIntegrationsByIdMergeFields', {
                        id: this.conversation.contact.id,
                        params: params,
                    });
                } else {
                    response = await this.$store.dispatch('getIntegrationsByIdFields', {
                        id: this.integration.key,
                        params: params,
                        update: false,
                    });
                }

                this.results = response;
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'handleSearch');
            } finally {
                this.busy = false;
            }
        },

        ucFirst(str) {
            if (!str) return str;

            return str[0].toUpperCase() + str.slice(1);
        },

        ucFirstAndLast(str) {
            if (!str) return str;

            return str
                .split(' ')
                .map((item) => item[0].toUpperCase() + item.slice(1))
                .join(' ');
        },

        switchName(name, value) {
            switch (name) {
                case 'first_name':
                case 'firstname':
                case 'FirstName':
                case 'last_name':
                case 'lastname':
                case 'LastName':
                    return this.ucFirst(value);
                case 'name':
                    return this.ucFirstAndLast(value);
            }
        },

        applyMergeField(item) {
            if (this.hasNotIntegration(item)) return;

            if (item.value && this.switchName(item.name, item.value)) {
                item.value = this.switchName(item.name, item.value);
            }

            if (item.template) {
                this.query = '';

                return;
            }

            this.query = '';

            const value = this.template
                ? this.checkDate(item.template).join(' ')
                : this.checkDate(item.value).join(' ');

            this.$emit('selected', value, item);
            this.$emit('closed');
        },

        checkDate(_str) {
            if (!_str) return [];

            let strSplit = String(_str).split(' ');
            let endValue = strSplit.map((e) => {
                if (moment(e, 'YYYY-MM-DD').format('YYYY-MM-DD') === e) {
                    return moment(e).format('MM/DD/YYYY');
                }
                return e;
            });
            return endValue;
        },

        hasNotIntegration(item) {
            if (!item.integrationKey) return false;

            return !this.hasIntegrationUser(item.integrationKey);
        },
    },

    mounted() {
        this.debounceSearch = debounce(this.handleSearch, 500);
    },
};
</script>
